import React, { useEffect, useState } from "react";
import ListingTable from "../../component/ListingTable";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import Drawer from "../../component/Drawer";
import ServiceIssue from "./service-issue";
import ServiceComplete from "./service-complete";
import Services from "../../../../api/services";
interface Column {
  id: "serviceName" | "customerName" | "address" | "schedule" | "status";
  label: string;
  minWidth?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api/v1";

const columns: readonly Column[] = [
  { id: "serviceName", label: "Service Name", minWidth: 170 },
  { id: "customerName", label: "Customer Name", minWidth: 100 },
  { id: "address", label: "Address", minWidth: 170 },
  { id: "schedule", label: "Schedule", minWidth: 170 },
  { id: "status", label: "Status", minWidth: 170 },
];

interface Data {
  id: number;
  serviceName: string;
  customerName: string;
  address: string;
  schedule: string;
  status: React.ReactNode;
}

function createData(
  id: number,
  serviceName: string,
  customerName: string,
  address: string,
  schedule: string,
  status: React.ReactNode
): Data {
  return { id, serviceName, customerName, address, schedule, status };
}

const ServiceHistory: React.FC = () => {
  const [serviceHistory, setServiceHistory] = useState([]);
  const [serviceBookingId, setServiceBookingId] = useState(0);
  const [serviceComplete, setServiceComplete] = useState(false);
  const [serviceIssue, setServiceIssue] = useState(false);

  // --- NEW: OTP State Variables ---
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);

  //  Feedback form close handler

  // const [feedBackForm , setFeedbackForm] = useState(false);

  const handleInitiateCompletion = (bookingId: number, status: string) => {
    setServiceBookingId(bookingId);
    setOtpValue("");
    setOtpError("");
    if (status === "paid") {
      setIsOtpDialogOpen(false); // Open OTP Dialog
      setServiceComplete(true);  // Open Feedback Form Drawer directly

    } else {
      setIsOtpDialogOpen(true); // Open OTP Dialog

    }
  }

  //  Implementing the capturePaymentIntent only when the service completes and check OTP

  const capturePaymentIntentAndOTPValidate = async (bookingId: any) => {
    const response = await fetch(
      `${API_BASE_URL}/stripe/capture-payment-intent`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId, OTP: otpValue }),
      }
    );
    const data = await response.json();
    return data;
  };


  // THis function handles OTP verification and payment capture
  const handleVerifyOTPAndPaymentCapture = async () => {
    if (!otpValue || otpValue.length !== 6) {
      setOtpError("Please enter a valid 6-digit code.");
      return;
    }

    // console.log("Verifying OTP for booking ID:", serviceBookingId, "with OTP:", otpValue);
    setVerifying(true);
    setOtpError("");

    try {
      const tokenString = localStorage.getItem("access_token");

      // Example API Call:

      //  Working on the feature...
      // const tokenString = localStorage.getItem("access_token");
      if (tokenString !== null) {
        const token = JSON.parse(tokenString);

        // const options = {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        // };

        const captureResult = await capturePaymentIntentAndOTPValidate(serviceBookingId);

        if (
          captureResult.paymentIntent === 1 ||
          captureResult.paymentIntent.status === "succeeded"
        )

          setIsOtpDialogOpen(false); // Close OTP Dialog
        setServiceComplete(true);  // Open Feedback Form Drawer directly
        setVerifying(false)
        // setFeedbackForm(true);  
      }
    }
    catch (error: any) {
      console.error("OTP Verification failed:", error);
      setOtpError(error.response?.data?.message || "OTP Verification failed. Please try again.");
    } finally {
      // 4. STOP SPINNER (This runs always: success, error, or logic fail)
      setVerifying(false);
    }
  }
  const handleCloseFeedback = () => {
    setServiceComplete(false);
  };

  const handleComplete = (bookingId: number) => {
    setServiceBookingId(bookingId);
    setServiceComplete(!serviceComplete);
  };

  const handleIssue = (bookingId: number) => {
    setServiceBookingId(bookingId);
    setServiceIssue(!serviceIssue);
  };

  const rows = serviceHistory.map((item: any, index: number) => {
    const rowData = {
      index: index + 1,
      serviceName: item.service_name,
      customerName: item.name,
      address: item.address,
      serviceDateTime: item.service_date_time,
      bookingId: item.id,
      status: item.status,
    };

    return createData(
      rowData.index,
      rowData.serviceName,
      rowData.customerName,
      rowData.address,
      rowData.serviceDateTime,
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        {rowData.status === "accepted" || rowData.status === "paid" ? (
          <>
            <Button
              variant="contained"
              onClick={() => handleInitiateCompletion(rowData.bookingId, rowData.status)}
              sx={{
                "&.MuiButtonBase-root": {
                  fontSize: "12px",
                },
              }}
            >
              Complete
            </Button>

            <Button
              onClick={() => handleIssue(rowData.bookingId)}
              sx={{
                background: "#ef5350",
                color: "#fff",
                borderRadius: "30px",
                textTransform: "uppercase",
                padding: "6px 16px",
                fontSize: "12px",
                "&:hover": {
                  background: "#ef5350",
                  color: "#fff",
                },
              }}
            >
              Issue
            </Button>
          </>
        ) : rowData.status === "completed" ? (
          <Box component={"span"} sx={{ color: "green" }}>
            Completed
          </Box>
        ) : (
          <Box component={"span"} sx={{ color: "red" }}>
            Issue
          </Box>
        )}
      </Stack>
    );
  });

  const fetchServiceHistoryData = async () => {
    try {
      const tokenString = localStorage.getItem("access_token");
      if (tokenString !== null) {
        const token = JSON.parse(tokenString);
        const options = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await Services.provider.getServiceHistoryData(options);
        setServiceHistory(response?.data.data);
        // console.log("Service History Data:", response.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchServiceHistoryData();
  }, [verifying]);

  return (
    <>
      <Box sx={{ py: 2 }}>
        <Typography variant="h2" sx={{ fontSize: "24px", mb: 3 }}>
          Service History
        </Typography>
        <ListingTable columns={columns} rows={rows} />
      </Box>

      {/* Otp verificaton section */}
      <Dialog
        open={isOtpDialogOpen}
        onClose={() => setIsOtpDialogOpen(false)}
      >
        <DialogTitle> Verify OTP </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Ask the customer for the OTP to complete the service and capture payment.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Enter 6-digit OTP"
            type="text"
            fullWidth
            variant="outlined"
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            error={!!otpError}
            helperText={otpError}
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsOtpDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOTPAndPaymentCapture}
            variant="contained"
            disabled={verifying}
          >
            {verifying ? <CircularProgress size={24} /> : "Verify & Complete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        isOpen={serviceComplete}
        handleClose={() => handleComplete(serviceBookingId)}
        title="Feedback Form"
      // feedbackForm={feedBackForm}
      >
        <ServiceComplete
          serviceBookingId={serviceBookingId}
          // setServiceHistory={setServiceHistory}
          onSuccess={(data) => setServiceHistory(data)}
          handleClose={() => handleComplete(serviceBookingId)}
        />
      </Drawer>
      <Drawer
        isOpen={serviceIssue}
        handleClose={() => handleIssue(serviceBookingId)}
        title="Service Issue"
      >
        <ServiceIssue
          handleClose={() => handleIssue(serviceBookingId)}
          serviceBookingId={serviceBookingId}
          // setServiceHistory={setServiceHistory}
          onSuccess={(data) => setServiceHistory(data)}
        />
      </Drawer>
    </>
  );
};

export default ServiceHistory;

